import React, {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    Select,
    SimpleGrid,
    Skeleton,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr
} from "@chakra-ui/react";
import {useSearchParams} from "react-router-dom";
import axios from "../axios/axios";
import {Header} from "../components/Header";
import formatHoursAndMinutes from "../lib/helpers/formatHoursAndMinutes";

function getLastFiscalYearRange(date = new Date()) {
    const year = date.getFullYear();
    const fiscalYearEnd = new Date(year, 2, 31);
    const endYear = date > fiscalYearEnd ? year : year - 1;

    return {
        start_date: `${endYear - 1}-04-01`,
        end_date: `${endYear}-03-31`
    };
}

function getAsanaTaskUrl(taskGid) {
    if (!taskGid) return null;
    return `https://app.asana.com/0/0/${taskGid}/f`;
}

function getAsanaSectionUrl(sectionGid) {
    if (!sectionGid) return null;
    return `https://app.asana.com/0/0/${sectionGid}/f`;
}

export default function AdminBrandsContainer() {
    const [searchParams, setSearchParams] = useSearchParams();
    const fiscalYearRange = useMemo(() => getLastFiscalYearRange(), []);

    const start_date = searchParams.get("start_date") || fiscalYearRange.start_date;
    const end_date = searchParams.get("end_date") || fiscalYearRange.end_date;
    const portfolio = searchParams.get("portfolio") || "";

    const [rows, setRows] = useState([]);
    const [portfolios, setPortfolios] = useState([]);
    const [totalMinutes, setTotalMinutes] = useState(0);
    const [loading, setLoading] = useState(true);
    const [selectedDetails, setSelectedDetails] = useState(null);

    const updateSearchParams = updates => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);

            Object.entries(updates).forEach(([key, value]) => {
                if (!value) next.delete(key);
                else next.set(key, String(value));
            });

            return next;
        });
    };

    const applyFiscalYearFilter = () => {
        updateSearchParams(fiscalYearRange);
    };

    useEffect(() => {
        const queryParams = new URLSearchParams();
        if (start_date) queryParams.append("start_date", start_date);
        if (end_date) queryParams.append("end_date", end_date);
        if (portfolio) queryParams.append("portfolio", portfolio);

        setLoading(true);

        axios
            .get(`/admin/brands-time?${queryParams.toString()}`)
            .then(response => {
                setRows(response.data.rows || []);
                setPortfolios(response.data.portfolios || []);
                setTotalMinutes(Number(response.data.total_minutes || 0));
            })
            .catch(error => {
                console.error("Error fetching brand time:", error);
                setRows([]);
                setTotalMinutes(0);
            })
            .finally(() => setLoading(false));
    }, [start_date, end_date, portfolio]);

    return (
        <Box width="full">
            <Header fontSize={{base: "xl", md: "2xl"}}>
                Marques
            </Header>

            <Stack
                direction={{base: "column", lg: "row"}}
                width="full"
                spacing={6}
                alignItems="start"
                mt={5}
            >
                <Stack flex="1" width="full" minW={0}>
                    <Text fontWeight="bold">
                        Temps interne et calendriers editoriaux par collaborateur
                    </Text>

                    {loading ? (
                        <Stack spacing={3}>
                            {[...Array(8)].map((_, index) => (
                                <Skeleton key={index} height="36px"/>
                            ))}
                        </Stack>
                    ) : rows.length === 0 ? (
                        <Text color="red.500">Aucun temps trouve sur cette periode</Text>
                    ) : (
                        rows.map(row => (
                            <Box key={row.user_gid_asana || row.user_name} mb={6}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                                    <Text fontSize="md" fontWeight="bold">
                                        {row.user_name || "Sans collaborateur"} -{" "}
                                        {formatHoursAndMinutes(Number(row.time_spent || 0) / 60)}
                                    </Text>
                                    {!row.user_gid_asana && row.details?.length > 0 && (
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => setSelectedDetails(row.details)}
                                        >
                                            Voir le detail
                                        </Button>
                                    )}
                                </Stack>

                                <TableContainer width="full" overflowX="auto">
                                    <Table variant="simple" size={{base: "sm", md: "md"}} sx={{tableLayout: "fixed"}}>
                                        <Thead>
                                            <Tr>
                                                <Th>Marque / client</Th>
                                                <Th width="140px" isNumeric>Temps</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {(row.brands || []).map(brand => (
                                                <Tr key={`${row.user_gid_asana}-${brand.client_id}`}>
                                                    <Td overflow="hidden" textOverflow="ellipsis" whiteSpace="nowrap">
                                                        {brand.client_name}
                                                        {brand.portfolio_name_asana && (
                                                            <Text as="span" fontSize="sm" fontStyle="italic">
                                                                {" "}({brand.portfolio_name_asana})
                                                            </Text>
                                                        )}
                                                    </Td>
                                                    <Td isNumeric whiteSpace="nowrap">
                                                        {formatHoursAndMinutes(Number(brand.time_spent || 0) / 60)}
                                                    </Td>
                                                </Tr>
                                            ))}
                                        </Tbody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        ))
                    )}
                </Stack>

                <Stack width={{base: "full", lg: "320px"}} flexShrink={0}>
                    <Text>
                        Total :{" "}
                        <Text as="span" fontWeight="bold">
                            {formatHoursAndMinutes(totalMinutes / 60)}
                        </Text>
                    </Text>

                    {rows.length > 0 && (
                        <Stack spacing={1}>
                            <Text fontWeight="bold">Recap par collaborateur</Text>
                            {rows.map(row => (
                                <Stack
                                    key={row.user_gid_asana || row.user_name}
                                    direction="row"
                                    justifyContent="space-between"
                                    fontSize="sm"
                                    spacing={3}
                                >
                                    <Text noOfLines={1}>{row.user_name || "Sans collaborateur"}</Text>
                                    <Text fontWeight="bold">
                                        {formatHoursAndMinutes(Number(row.time_spent || 0) / 60)}
                                    </Text>
                                </Stack>
                            ))}
                        </Stack>
                    )}

                    <Button variant="outline" onClick={applyFiscalYearFilter}>
                        Bilan fiscal {fiscalYearRange.start_date.slice(0, 4)}-
                        {fiscalYearRange.end_date.slice(0, 4)}
                    </Button>

                    <Text fontWeight="bold">Portefeuille</Text>
                    <Select
                        placeholder="Interne + Calendriers editoriaux"
                        value={portfolio}
                        onChange={event => updateSearchParams({portfolio: event.target.value})}
                    >
                        {portfolios.map(item => (
                            <option key={item} value={item}>{item}</option>
                        ))}
                    </Select>

                    <SimpleGrid columns={{base: 1, md: 2}} spacing={4}>
                        <Box>
                            <Text fontWeight="bold">Date debut</Text>
                            <Input
                                type="date"
                                value={start_date}
                                onChange={event => updateSearchParams({start_date: event.target.value})}
                            />
                        </Box>
                        <Box>
                            <Text fontWeight="bold">Date fin</Text>
                            <Input
                                type="date"
                                value={end_date}
                                onChange={event => updateSearchParams({end_date: event.target.value})}
                            />
                        </Box>
                    </SimpleGrid>
                </Stack>
            </Stack>

            <Modal
                isOpen={Boolean(selectedDetails)}
                onClose={() => setSelectedDetails(null)}
                size="5xl"
            >
                <ModalOverlay/>
                <ModalContent>
                    <ModalHeader>Temps sans collaborateur</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody pb={6}>
                        <Text mb={4}>
                            Ces lignes viennent d'Asana mais ne peuvent pas etre rattachees a une personne.
                            Le plus souvent, le temps est saisi sur la tache sans entree detaillee et la tache
                            n'a pas de responsable.
                        </Text>
                        <TableContainer>
                            <Table size="sm" variant="simple">
                                <Thead>
                                    <Tr>
                                        <Th>Client</Th>
                                        <Th>Devis / section</Th>
                                        <Th>Tache</Th>
                                        <Th isNumeric>Temps</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {(selectedDetails || []).map(detail => (
                                        <Tr key={`${detail.source}-${detail.task_gid}-${detail.project_id}-${detail.spent_on}`}>
                                            <Td>{detail.client_name}</Td>
                                            <Td>
                                                {getAsanaSectionUrl(detail.section_gid_asana) ? (
                                                    <Button
                                                        as="a"
                                                        href={getAsanaSectionUrl(detail.section_gid_asana)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        variant="link"
                                                        colorScheme="blue"
                                                        whiteSpace="normal"
                                                        textAlign="left"
                                                        height="auto"
                                                    >
                                                        {detail.project_name}
                                                    </Button>
                                                ) : (
                                                    detail.project_name
                                                )}
                                            </Td>
                                            <Td>
                                                {getAsanaTaskUrl(detail.task_gid) ? (
                                                    <Button
                                                        as="a"
                                                        href={getAsanaTaskUrl(detail.task_gid)}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        variant="link"
                                                        colorScheme="blue"
                                                        whiteSpace="normal"
                                                        textAlign="left"
                                                        height="auto"
                                                    >
                                                        {detail.task_name}
                                                    </Button>
                                                ) : (
                                                    detail.task_name
                                                )}
                                            </Td>
                                            <Td isNumeric whiteSpace="nowrap">
                                                {formatHoursAndMinutes(Number(detail.time_spent || 0) / 60)}
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </TableContainer>
                    </ModalBody>
                </ModalContent>
            </Modal>
        </Box>
    );
}
